import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import styles from 'components/medicalHistory/page.module.scss';
import Stack from '@mui/material/Stack';
import Button from 'components/common/button';
import UploaderElement from 'components/common/uploaderElement';
import { StateTypeLocalStorage, usePersistMHReducer } from 'reducer/medicalHistory';
import OutlineButton from 'components/common/outlineButton';
import AddIcon from '@mui/icons-material/Add';
import { NullableCommunique } from 'types/types';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import ClearIcon from '@mui/icons-material/Clear';
import MetaTag from 'components/meta';
import Label from 'components/common/label';
import useMediaUpload from 'hooks/useMediaUpload';
import clsx from 'clsx';
import { isEmptyString } from 'utils/helper';
import dayjs from 'dayjs';
import { MultimediaType } from 'utils/constants';

type FileWithType = {
  name: number;
  file?: string | undefined | null;
  fileType?: MultimediaType;
  isError?: boolean;
};

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

const initialValueOfFile: FileWithType = {
  name: 1,
  file: null,
};

function UploadRelevantMedicalRecords({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const { mediaUpload } = useMediaUpload();
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;

  const [state, dispatch] = usePersistMHReducer();

  const [valueOfFile, setValueOfFile] = useState<FileWithType[]>([
    initialValueOfFile,
  ]);

  useEffect(() => {
    if (state) {
      const relevantMedicalRecords = state.find(
        (p: StateTypeLocalStorage) => p.name === actionPage,
      );
      if (relevantMedicalRecords?.value?.length) {
        setValueOfFile(relevantMedicalRecords.value);
      }
    }
  }, [actionPage, state]);

  const clickNextOrSubmit = async (nextPage: number) => {
    const withErrorItem = valueOfFile.map((item) => {
      const checkFields = [item.file, item.fileType];
      if (!checkFields.every((value) => isEmptyString(value))
        && checkFields.some((value) => isEmptyString(value))) {
        item.isError = true;
        return item;
      }
      return item;
    });
    setValueOfFile(withErrorItem);
    const hasError = valueOfFile.some((item) => item.isError);
    if (hasError) {
      return;
    }

    valueOfFile.forEach((item, index) => {
      if (item.file && item.fileType) {
        const timeStamp = dayjs().unix() + Math.round(Math.random() * 1000);
        mediaUpload({
          patientId: communiqueData?.patientId,
          dataUrl: item.file,
          multimediaType: item.fileType as MultimediaType,
          extraName: `${item.fileType}-${timeStamp}-${index}`,
        });
      }
    });

    const validValueOfFile = valueOfFile.filter((item) => item.fileType);

    await dispatch({
      type: actionPage,
      name: 'Upload relevant medical records',
      value: validValueOfFile,
    });
    nextOrSubmitButton(nextPage);
  };

  const onSelectImage = (name: number, file: string | undefined) => {
    if (typeof file !== 'undefined') {
      setValueOfFile((old: FileWithType[]) => old.filter((e: FileWithType) => {
        if (e.name === name) {
          e.file = file;
          e.isError = false;
          return e;
        } return e;
      }));
    }
  };

  const onChangeDocumentType = (
    name: number,
    value: MultimediaType,
  ) => {
    setValueOfFile((old: FileWithType[]) => old.filter((e: FileWithType) => {
      if (e.name === name) {
        e.fileType = e.fileType !== value ? value : undefined;
        e.isError = false;
        return e;
      } return e;
    }));
  };

  const removeForm = (item: number) => {
    setValueOfFile((old) => old.filter((d, index) => index !== item));
  };

  const addButtonClass = clsx(valueOfFile.length <= 3 ? 'addButton' : 'addButtonDisable');

  return (
    <>
      <MetaTag title="Upload relevant medical records" />
      <Box pb={4} mb={4}>
        <Box>
          <h2 className={`marginTopZero ${styles.titleText}`}>
            Upload relevant medical records
          </h2>
          <p className={styles.titleTextSubText}>
            Doctor’s notes, images (e.g. x-rays), and lab results from previous
            visits with other providers related to your current concern
          </p>
          {[...Array(valueOfFile.length)].map((e, index) => {
            const item = valueOfFile.find((e) => e.name === index + 1);
            return (
              <Stack key={item?.name}>
                {index !== 0 && (
                  <div className="flex justify-end cursor-pointer mb-2">
                    <ClearIcon
                      onClick={() => removeForm(index)}
                      fontSize="small"
                      color="action"
                    />
                  </div>
                )}
                {item?.isError && (
                <p className="error-message mb-1">
                  Please select a related file type for the uploaded pdf to continue.
                </p>
                )}
                <UploaderElement
                  text="Upload your pdf here"
                  imageBase64={item?.file ?? undefined}
                  getImage={(file) => onSelectImage(index + 1, file)}
                  acceptExtensions="application/pdf"
                />
                <p className={styles.separator} />
                <Label>
                  What is this related to?
                </Label>
                <Stack
                  sx={{ display: 'flex', flexDirection: 'row' }}
                  gap={2}
                  mb={3}
                >
                  <OutlineButton
                    onClick={() => onChangeDocumentType(
                      index + 1,
                      MultimediaType.Xray,
                    )}
                    isActive={MultimediaType.Xray === item?.fileType}
                  >
                    Imaging
                  </OutlineButton>
                  <OutlineButton
                    onClick={() => onChangeDocumentType(
                      index + 1,
                      MultimediaType.LabReport,
                    )}
                    isActive={
                      MultimediaType.LabReport === item?.fileType
                    }
                  >
                    Lab
                  </OutlineButton>
                  <OutlineButton
                    onClick={() => onChangeDocumentType(
                      index + 1,
                      MultimediaType.OtherProviderNote,
                    )}
                    isActive={
                      MultimediaType.OtherProviderNote
                      === item?.fileType
                    }
                  >
                    Note
                  </OutlineButton>
                  <OutlineButton
                    onClick={() => onChangeDocumentType(
                      index + 1,
                      MultimediaType.Other,
                    )}
                    isActive={
                      MultimediaType.Other
                      === item?.fileType
                    }
                  >
                    Other
                  </OutlineButton>
                </Stack>
              </Stack>
            );
          })}
          <p className={styles.separator} />
          <button
            className={addButtonClass}
            onClick={() => {
              setValueOfFile((old) => [
                ...old,
                { name: valueOfFile.length + 1 },
              ]);
            }}
            type="button"
            disabled={valueOfFile.length === 4}
          >
            <AddIcon fontSize="small" />
            Add another file
          </button>
        </Box>
        <div className="next-button-fixed">
          <Button onClick={() => clickNextOrSubmit(nextPage)}>
            Next
          </Button>
        </div>
      </Box>
    </>
  );
}

export default UploadRelevantMedicalRecords;
